import React, { useState, useEffect, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  Row,
  RSelect,
} from "../../../components/Component";
import {
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Form,
} from "reactstrap";
import http from "../../../utils/http-common";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { API_BASE_URL } from "../../../utils/Utils";
import swal from "sweetalert";
import ConfirmModal from "../../../components/partials/user-manage/ConfirmModal";
import MediaSelector from "../../../components/partials/user-manage/MediaSelector";

const SLUG_CODE_REGEX = /^[a-z0-9_-]+$/;

const DEFAULT_SITE_BASE_URL = "https://inoqare.com";

/**
 * Base URL for the public site (landing, circle pages). Never use admin origin for shareable links.
 */
function getSiteBaseUrl() {
  if (typeof process !== "undefined") {
    const fromEnv = process.env?.REACT_APP_SITE_URL || process.env?.REACT_APP_PUBLIC_SITE_URL || process.env?.REACT_APP_PUBLIC_URL;
    if (fromEnv) return String(fromEnv).replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.location?.hostname) {
    const host = window.location.hostname.toLowerCase();
    if (host === "admin.inoqare.com" || host === "www.admin.inoqare.com") {
      return DEFAULT_SITE_BASE_URL;
    }
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://localhost:3000";
    }
    return window.location.origin;
  }
  return DEFAULT_SITE_BASE_URL;
}

function getCirclePublicLink(code) {
  if (!code || !String(code).trim()) return "";
  const base = getSiteBaseUrl();
  const segment = encodeURIComponent(String(code).trim().toLowerCase());
  return `${base}/circle/${segment}`;
}

/** Full URL for media display (backend returns relative paths like /uploads/media/xxx). */
function getMediaDisplayUrl(path) {
  if (!path || typeof path !== "string") return "";
  const p = path.trim();
  if (p.startsWith("http://") || p.startsWith("https://")) return p;
  const base = (API_BASE_URL || "").replace(/\/$/, "");
  return p.startsWith("/") ? `${base}${p}` : `${base}/${p}`;
}

const DISTRIBUTION_RULE_OPTIONS = [
  { value: "by_target", label: "Fill targets first — assign leads to clinics below their target" },
  { value: "round_robin", label: "Round robin — rotate leads equally between clinics" },
  { value: "weighted", label: "Weighted — distribute by each clinic's weight" },
  { value: "least_served", label: "Least served — prefer clinics that received fewer leads" },
];

const PERIOD_OPTIONS = [
  { value: "weekly", label: "Weekly — reset targets every week" },
  { value: "monthly", label: "Monthly — reset targets every month" },
  { value: "quarterly", label: "Quarterly — reset targets every quarter" },
];

const LeadCircles = () => {
  const [circles, setCircles] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clinics, setClinics] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCircle, setEditingCircle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    circle: null,
    loading: false,
  });
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [saveError, setSaveError] = useState("");

  const [form, setForm] = useState({
    name: "",
    code: "",
    country: "",
    specialty: "",
    distributionRule: "by_target",
    period: "monthly",
    active: true,
    clinicIds: [],
    targets: {},
    formOptionsRootCategoryId: "",
    bannerImageUrl: "",
    photos: [],
    beforeAfterCases: [],
    testimonialVideos: [],
  });
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [initialFormSnapshot, setInitialFormSnapshot] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [mediaSelectorOpen, setMediaSelectorOpen] = useState({ banner: false, photos: false, beforeAfter: false });
  const [copyFromClinic, setCopyFromClinic] = useState({ clinicId: "", copyBanner: true, copyPhotos: true, copyBeforeAfter: true });
  const [copyingMedia, setCopyingMedia] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingBeforeAfter, setUploadingBeforeAfter] = useState(false);

  const getFormSnapshot = (f) => JSON.stringify({
    name: (f.name || "").trim(),
    code: (f.code || "").trim(),
    country: (f.country || "").trim(),
    specialty: (f.specialty || "").trim(),
    distributionRule: f.distributionRule || "by_target",
    period: f.period || "monthly",
    active: f.active !== false,
    clinicIds: (f.clinicIds || []).slice().sort(),
    targets: JSON.stringify(f.targets || {}),
    formOptionsRootCategoryId: (f.formOptionsRootCategoryId || "").trim(),
    bannerImageUrl: (f.bannerImageUrl || "").trim(),
    photos: JSON.stringify(f.photos || []),
    beforeAfterCases: JSON.stringify((f.beforeAfterCases || []).map((c) => ({ title: c.title, imageUrl: c.imageUrl, order: c.order }))),
    testimonialVideos: JSON.stringify((f.testimonialVideos || []).map((v) => ({ url: v.url, title: v.title, thumbnail: v.thumbnail }))),
  });

  const isFormDirty = () => {
    if (!initialFormSnapshot) return false;
    return getFormSnapshot(form) !== initialFormSnapshot;
  };

  const fetchCircles = useCallback((signal) => {
    setLoading(true);
    setError("");
    const params = includeInactive ? {} : { active: "true" };
    http
      .get("/api/v1/lead-circles", { signal, params })
      .then((res) => {
        const data = res.data?.data ?? res.data?.circles ?? res.data;
        setCircles(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setError(e.response?.data?.message || "Failed to load lead circles");
        setCircles([]);
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, [includeInactive]);

  useEffect(() => {
    const ac = new AbortController();
    fetchCircles(ac.signal);
    return () => ac.abort();
  }, [fetchCircles]);

  useEffect(() => {
    const ac = new AbortController();
    http
      .get("/api/v1/partners/medical/clinics", { signal: ac.signal })
      .then((res) => {
        const list = res.data?.data ?? res.data?.clinics ?? res.data ?? [];
        setClinics(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (!isAbortError(e)) setClinics([]);
      });
    return () => ac.abort();
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    http
      .get("/api/v1/category/public", { signal: ac.signal })
      .then((res) => {
        const list = res.data?.data ?? [];
        setCategoryOptions(
          Array.isArray(list)
            ? list.map((c) => ({ value: c._id, label: c.label || `Category ${c.uid || c._id}` }))
            : []
        );
      })
      .catch((e) => {
        if (!isAbortError(e)) setCategoryOptions([]);
      });
    return () => ac.abort();
  }, []);

  const clinicOptions = clinics.map((c) => ({
    value: c._id || c.id,
    label: [c.name, c.address?.city, c.address?.country].filter(Boolean).join(" — ") || c._id,
    raw: c,
  }));

  const openCreate = () => {
    setSaveError("");
    setEditingCircle(null);
    const emptyForm = {
      name: "",
      code: "",
      country: "",
      specialty: "",
      distributionRule: "by_target",
      period: "monthly",
      active: true,
      clinicIds: [],
      targets: {},
      formOptionsRootCategoryId: "",
      bannerImageUrl: "",
      photos: [],
      beforeAfterCases: [],
      testimonialVideos: [],
    };
    setForm(emptyForm);
    setInitialFormSnapshot(getFormSnapshot(emptyForm));
    setModalOpen(true);
  };

  const openEdit = (circle) => {
    setSaveError("");
    const clinicIds = Array.isArray(circle.clinicIds) ? circle.clinicIds.map((id) => (typeof id === "object" ? id._id || id.id : id)) : [];
    const targetsMap = {};
    (circle.targets || []).forEach((t) => {
      const cid = t.clinicId?._id || t.clinicId?.id || t.clinicId;
      if (cid) {
        targetsMap[cid] = {
          targetLeads: t.targetLeads ?? 0,
          maxLeads: t.maxLeads ?? "",
          weight: t.weight ?? "",
          priority: t.priority ?? 0,
          budgetCap: t.budgetCap ?? "",
        };
      }
    });
    clinicIds.forEach((cid) => {
      if (!targetsMap[cid]) targetsMap[cid] = { targetLeads: 0, maxLeads: "", weight: "", priority: 0, budgetCap: "" };
    });
    setEditingCircle(circle);
    const formOptionsRootCategoryId = circle.formOptionsRootCategoryId
      ? (typeof circle.formOptionsRootCategoryId === "string" ? circle.formOptionsRootCategoryId : circle.formOptionsRootCategoryId?._id || "")
      : "";
    const bannerImageUrl = typeof circle.bannerImageUrl === "string" ? circle.bannerImageUrl.trim() : (circle.banner ? (typeof circle.banner === "string" ? circle.banner : circle.banner?.url || circle.banner?.fileUrl || "") : "");
    const photos = Array.isArray(circle.photos) ? circle.photos.map((p) => (typeof p === "string" ? p : p?.url || p?.fileUrl || "")).filter(Boolean) : [];
    const beforeAfterCases = Array.isArray(circle.beforeAfterCases)
      ? circle.beforeAfterCases.map((c) => ({
          title: c.title != null ? String(c.title) : "",
          imageUrl: typeof c.imageUrl === "string" ? c.imageUrl : c.imageUrl?.url || "",
          order: typeof c.order === "number" ? c.order : parseInt(c.order, 10) || 0,
        }))
      : [];
    const testimonialVideos = Array.isArray(circle.testimonialVideos)
      ? circle.testimonialVideos.map((v) => ({
          url: typeof v.url === "string" ? v.url : "",
          title: v.title != null ? String(v.title) : "",
          thumbnail: v.thumbnail != null ? String(v.thumbnail) : "",
        })).filter((v) => v.url.trim())
      : [];
    const formData = {
      name: circle.name || "",
      code: circle.code || "",
      country: circle.country || "",
      specialty: circle.specialty || "",
      distributionRule: circle.distributionRule || "by_target",
      period: circle.period || "monthly",
      active: circle.active !== false,
      clinicIds,
      targets: targetsMap,
      formOptionsRootCategoryId,
      bannerImageUrl: bannerImageUrl || "",
      photos,
      beforeAfterCases,
      testimonialVideos,
    };
    setForm(formData);
    setInitialFormSnapshot(getFormSnapshot(formData));
    setModalOpen(true);
  };

  const closeModal = () => {
    setSaveError("");
    setModalOpen(false);
    setEditingCircle(null);
    setInitialFormSnapshot(null);
  };

  const handleRequestCloseModal = () => {
    if (isFormDirty()) {
      setShowDiscardConfirm(true);
    } else {
      closeModal();
    }
  };

  const handleConfirmDiscard = () => {
    closeModal();
    setShowDiscardConfirm(false);
  };

  const onFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onClinicsChange = (selected) => {
    const selectedIds = (selected || []).map((s) => s.value);
    setForm((prev) => {
      const nextTargets = { ...prev.targets };
      prev.clinicIds.forEach((id) => {
        if (!selectedIds.includes(id)) delete nextTargets[id];
      });
      selectedIds.forEach((id) => {
        if (nextTargets[id] == null) {
          nextTargets[id] = { targetLeads: 0, maxLeads: "", weight: "", priority: 0, budgetCap: "" };
        }
      });
      return { ...prev, clinicIds: selectedIds, targets: nextTargets };
    });
  };

  const onTargetChange = (clinicId, field, value) => {
    setForm((prev) => {
      const next = { ...prev.targets };
      next[clinicId] = { ...(next[clinicId] || {}), [field]: value };
      return { ...prev, targets: next };
    });
  };

  const selectedClinicOptions = form.clinicIds
    .map((id) => clinicOptions.find((o) => o.value === id))
    .filter(Boolean);

  const buildPayload = () => {
    const targets = form.clinicIds
      .filter((id) => id)
      .map((clinicId) => {
        const t = form.targets[clinicId] || {};
        return {
          clinicId,
          targetLeads: Number(t.targetLeads) || 0,
          maxLeads: t.maxLeads !== "" && t.maxLeads != null ? Number(t.maxLeads) : null,
          weight: t.weight !== "" && t.weight != null ? Number(t.weight) : null,
          priority: Number(t.priority) || 0,
          budgetCap: t.budgetCap !== "" && t.budgetCap != null ? Number(t.budgetCap) : null,
        };
      });
    const beforeAfterPayload = (form.beforeAfterCases || [])
      .filter((c) => (c.imageUrl || "").trim())
      .map((c, idx) => ({
        title: (c.title || "").trim(),
        imageUrl: (c.imageUrl || "").trim(),
        order: typeof c.order === "number" ? c.order : idx,
      }));
    const testimonialVideosPayload = (form.testimonialVideos || [])
      .filter((v) => (v.url || "").trim())
      .map((v) => ({
        url: (v.url || "").trim(),
        title: (v.title || "").trim(),
        thumbnail: (v.thumbnail || "").trim(),
      }));
    return {
      name: (form.name || "").trim(),
      code: (form.code || "").trim().toLowerCase(),
      country: (form.country || "").trim(),
      specialty: (form.specialty || "").trim() || null,
      distributionRule: form.distributionRule || "by_target",
      period: form.period || "monthly",
      active: form.active,
      clinicIds: form.clinicIds,
      targets,
      formOptionsRootCategoryId: (form.formOptionsRootCategoryId && String(form.formOptionsRootCategoryId).trim()) || null,
      bannerImageUrl: (form.bannerImageUrl || "").trim() || null,
      photos: (form.photos || []).map((p) => (typeof p === "string" ? p : p.url || p.fileUrl || "").trim()).filter(Boolean),
      beforeAfterCases: beforeAfterPayload,
      testimonialVideos: testimonialVideosPayload,
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaveError("");

    const payload = buildPayload();
    if (!payload.name || !payload.code || !payload.country) {
      setSaveError("Please fill in name, code and country.");
      return;
    }

    const normalizedCode = payload.code.trim().toLowerCase();
    if (!SLUG_CODE_REGEX.test(normalizedCode)) {
      setSaveError("Code must contain only lowercase letters, numbers, hyphens and underscores (no spaces).");
      return;
    }

    if (!payload.clinicIds || payload.clinicIds.length === 0) {
      setSaveError("Select at least one clinic.");
      return;
    }

    setSaving(true);
    const promise = editingCircle
      ? http.put(`/api/v1/lead-circles/${editingCircle._id || editingCircle.id}`, payload)
      : http.post("/api/v1/lead-circles", payload);
    promise
      .then(() => {
        fetchCircles();
        closeModal();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          (typeof err.response?.data === "string" ? err.response.data : null) ||
          err.message ||
          "Failed to save lead circle.";
        setSaveError(message);
        swal("Error", message, "error");
      })
      .finally(() => setSaving(false));
  };

  const handleDeleteClick = (circle) => {
    setDeleteConfirm({ isOpen: true, circle, loading: false });
  };

  const handleDeleteConfirm = () => {
    const { circle } = deleteConfirm;
    if (!circle) return;
    const id = circle._id || circle.id;
    if (!id) {
      swal("Error", "Cannot delete: circle has no ID.", "error");
      return;
    }
    setDeleteConfirm((prev) => ({ ...prev, loading: true }));
    http
      .delete(`/api/v1/lead-circles/${id}`)
      .then(() => {
        fetchCircles();
        setDeleteConfirm({ isOpen: false, circle: null, loading: false });
        swal(
          "Circle deactivated",
          "The public landing URL will return 404. This list shows active circles only — use “Show deactivated circles” to see it again. Bots and ad campaigns may still request the old URL until you update them.",
          "success"
        );
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          (typeof err.response?.data === "string" ? err.response.data : null) ||
          err.message ||
          "Delete failed.";
        setDeleteConfirm((prev) => ({ ...prev, loading: false }));
        swal("Error", message, "error");
      });
  };

  const getClinicName = (clinicId) => {
    const opt = clinicOptions.find((o) => o.value === clinicId);
    return opt?.label ?? clinicId;
  };

  const handleCopyLink = (link, feedbackKey) => {
    if (!link) return;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopyFeedback(feedbackKey);
        setTimeout(() => setCopyFeedback(null), 2000);
      })
      .catch(() => {});
  };

  const refetchEditingCircle = useCallback(() => {
    if (!editingCircle) return;
    const id = editingCircle._id || editingCircle.id;
    http
      .get("/api/v1/lead-circles")
      .then((res) => {
        const data = res.data?.data ?? res.data?.circles ?? res.data;
        const list = Array.isArray(data) ? data : [];
        const updated = list.find((c) => (c._id || c.id) === id);
        if (updated) {
          setEditingCircle(updated);
          const photos = Array.isArray(updated.photos) ? updated.photos.map((p) => (typeof p === "string" ? p : p?.url || p?.fileUrl || "")).filter(Boolean) : [];
          const beforeAfterCases = Array.isArray(updated.beforeAfterCases)
            ? updated.beforeAfterCases.map((c) => ({
                title: c.title != null ? String(c.title) : "",
                imageUrl: typeof c.imageUrl === "string" ? c.imageUrl : c.imageUrl?.url || "",
                order: typeof c.order === "number" ? c.order : parseInt(c.order, 10) || 0,
              }))
            : [];
          const testimonialVideos = Array.isArray(updated.testimonialVideos)
            ? updated.testimonialVideos.map((v) => ({
                url: typeof v.url === "string" ? v.url : "",
                title: v.title != null ? String(v.title) : "",
                thumbnail: v.thumbnail != null ? String(v.thumbnail) : "",
              })).filter((v) => v.url.trim())
            : [];
          setForm((prev) => ({ ...prev, photos, beforeAfterCases, testimonialVideos }));
        }
      })
      .catch(() => {});
  }, [editingCircle]);

  const handleCopyMediaFromClinic = () => {
    const circleId = editingCircle?._id || editingCircle?.id;
    if (!circleId || !copyFromClinic.clinicId) {
      swal("Error", "Select a clinic to copy from.", "error");
      return;
    }
    const copyTypes = [];
    if (copyFromClinic.copyPhotos) copyTypes.push("photos");
    if (copyFromClinic.copyBeforeAfter) copyTypes.push("beforeAfterCases");
    if (copyTypes.length === 0) {
      swal("Error", "Select at least one type (Photos or Before & after cases).", "error");
      return;
    }
    setCopyingMedia(true);
    http
      .put(`/api/v1/lead-circles/${circleId}`, {
        copyMediaFromClinicId: copyFromClinic.clinicId,
        copyTypes,
      })
      .then(() => {
        swal("Success", "Photos and before/after cases have been copied. You can change or remove them below.", "success");
        refetchEditingCircle();
      })
      .catch((err) => {
        const msg = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to copy media.";
        swal("Error", msg, "error");
      })
      .finally(() => setCopyingMedia(false));
  };

  const handlePhotosFromLibrary = (selectedItems) => {
    const urls = (selectedItems || []).map((item) => item.fileUrl).filter(Boolean);
    if (urls.length) {
      setForm((prev) => ({ ...prev, photos: [...(prev.photos || []), ...urls] }));
      swal("Success", `${urls.length} photo(s) added.`, "success");
    }
    setMediaSelectorOpen((prev) => ({ ...prev, photos: false }));
  };

  const handleBeforeAfterFromLibrary = (selectedItems) => {
    const items = (selectedItems || []).filter((item) => item.fileUrl);
    if (items.length === 0) {
      setMediaSelectorOpen((prev) => ({ ...prev, beforeAfter: false }));
      return;
    }
    setForm((prev) => {
      const cases = (prev.beforeAfterCases || []).slice();
      const maxOrder = cases.length > 0 ? Math.max(...cases.map((c) => Number(c.order) ?? 0)) : 0;
      items.forEach((item, i) => {
        cases.push({ title: item.title || "", imageUrl: item.fileUrl, order: maxOrder + 1 + i });
      });
      return { ...prev, beforeAfterCases: cases };
    });
    swal("Success", `${items.length} image(s) added.`, "success");
    setMediaSelectorOpen((prev) => ({ ...prev, beforeAfter: false }));
  };

  const handleUploadPhotoFile = (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !/^image\//i.test(file.type)) {
      swal("Error", "Please select an image file.", "error");
      return;
    }
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("usage", "clinic");
    const circleId = editingCircle?._id || editingCircle?.id;
    const mediaUrl = circleId
      ? `/api/v1/media?ownerType=leadCircle&ownerId=${encodeURIComponent(circleId)}`
      : "/api/v1/media";
    http
      .post(mediaUrl, formData)
      .then((res) => {
        const url = res.data?.data?.fileUrl || res.data?.fileUrl || res.data?.url;
        if (url) {
          setForm((prev) => ({ ...prev, photos: [...(prev.photos || []), url] }));
          swal("Success", "Photo added.", "success");
        } else {
          swal("Error", "Upload succeeded but no URL returned.", "error");
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Upload failed.";
        swal("Error", msg, "error");
      })
      .finally(() => {
        setUploadingPhoto(false);
        e.target.value = "";
      });
  };

  const removePhoto = (index) => {
    setForm((prev) => {
      const next = (prev.photos || []).filter((_, i) => i !== index);
      return { ...prev, photos: next };
    });
  };

  const handleUploadBannerFile = (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !/^image\//i.test(file.type)) {
      swal("Error", "Please select an image file.", "error");
      return;
    }
    setUploadingBanner(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("usage", "banner");
    const circleId = editingCircle?._id || editingCircle?.id;
    const mediaUrl = circleId
      ? `/api/v1/media?ownerType=leadCircle&ownerId=${encodeURIComponent(circleId)}`
      : "/api/v1/media";
    http
      .post(mediaUrl, formData)
      .then((res) => {
        const url = res.data?.data?.fileUrl || res.data?.fileUrl || res.data?.url;
        if (url) {
          setForm((prev) => ({ ...prev, bannerImageUrl: url }));
          swal("Success", "Banner image set.", "success");
        } else {
          swal("Error", "Upload succeeded but no URL returned.", "error");
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Upload failed.";
        swal("Error", msg, "error");
      })
      .finally(() => {
        setUploadingBanner(false);
        e.target.value = "";
      });
  };

  const handleBannerFromLibrary = (selectedItems) => {
    const url = (selectedItems || [])[0]?.fileUrl;
    if (url) {
      setForm((prev) => ({ ...prev, bannerImageUrl: url }));
      swal("Success", "Banner image set.", "success");
    }
    setMediaSelectorOpen((prev) => ({ ...prev, banner: false }));
  };

  const updateBeforeAfterCase = (index, field, value) => {
    setForm((prev) => {
      const next = (prev.beforeAfterCases || []).map((c, i) => (i === index ? { ...c, [field]: value } : c));
      return { ...prev, beforeAfterCases: next };
    });
  };

  const removeBeforeAfterCase = (index) => {
    setForm((prev) => ({
      ...prev,
      beforeAfterCases: (prev.beforeAfterCases || []).filter((_, i) => i !== index),
    }));
  };

  const addBeforeAfterCase = () => {
    const cases = form.beforeAfterCases || [];
    const nextOrder = cases.length > 0 ? Math.max(...cases.map((c) => Number(c.order) ?? 0)) + 1 : 0;
    setForm((prev) => ({
      ...prev,
      beforeAfterCases: [...(prev.beforeAfterCases || []), { title: "", imageUrl: "", order: nextOrder }],
    }));
  };

  const addTestimonialVideo = () => {
    setForm((prev) => ({
      ...prev,
      testimonialVideos: [...(prev.testimonialVideos || []), { url: "", title: "", thumbnail: "" }],
    }));
  };

  const updateTestimonialVideo = (index, field, value) => {
    setForm((prev) => {
      const next = (prev.testimonialVideos || []).map((v, i) => (i === index ? { ...v, [field]: value } : v));
      return { ...prev, testimonialVideos: next };
    });
  };

  const removeTestimonialVideo = (index) => {
    setForm((prev) => ({
      ...prev,
      testimonialVideos: (prev.testimonialVideos || []).filter((_, i) => i !== index),
    }));
  };

  const handleUploadBeforeAfterFile = (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !/^image\//i.test(file.type)) {
      swal("Error", "Please select an image file.", "error");
      return;
    }
    setUploadingBeforeAfter(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("usage", "clinic");
    const circleId = editingCircle?._id || editingCircle?.id;
    const mediaUrl = circleId
      ? `/api/v1/media?ownerType=leadCircle&ownerId=${encodeURIComponent(circleId)}`
      : "/api/v1/media";
    http
      .post(mediaUrl, formData)
      .then((res) => {
        const url = res.data?.data?.fileUrl || res.data?.fileUrl || res.data?.url;
        if (url) {
          setForm((prev) => {
            const cases = prev.beforeAfterCases || [];
            const nextOrder = cases.length > 0 ? Math.max(...cases.map((c) => Number(c.order) ?? 0)) + 1 : 0;
            return { ...prev, beforeAfterCases: [...cases, { title: "", imageUrl: url, order: nextOrder }] };
          });
          swal("Success", "Image added.", "success");
        } else {
          swal("Error", "Upload succeeded but no URL returned.", "error");
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Upload failed.";
        swal("Error", msg, "error");
      })
      .finally(() => {
        setUploadingBeforeAfter(false);
        e.target.value = "";
      });
  };

  return (
    <>
      <Head title="Lead Circles" />
      <Content>
        <Block size="lg">
          <BlockBetween>
            <BlockHead>
              <BlockHeadContent>
                <BlockTitle tag="h4">Lead Circles</BlockTitle>
                <BlockDes>
                  Manage pub circles: assign clinics and set lead targets per clinic for distribution. Removing a circle
                  deactivates it (soft delete): it disappears from this list unless you show deactivated circles.
                </BlockDes>
              </BlockHeadContent>
            </BlockHead>
            <Button color="primary" onClick={openCreate}>
              <Icon name="plus" className="me-1" />
              Add circle
            </Button>
          </BlockBetween>

          {error && (
            <div className="alert alert-danger mt-3" role="alert">
              {error}
            </div>
          )}

          <div className="form-check mt-3 mb-0">
            <Input
              id="lead-circles-include-inactive"
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <Label className="form-check-label" for="lead-circles-include-inactive">
              Show deactivated circles
            </Label>
            <small className="text-soft d-block ms-4">
              Default view lists active circles only. Deactivating hides the public landing (404); crawlers and old ad URLs may still hit the API.
            </small>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <span className="spinner-border spinner-border-lg text-primary" />
              <p className="mt-2 text-soft">Loading lead circles...</p>
            </div>
          ) : (
            <div className="card card-bordered card-stretch mt-4">
              <div className="card-inner p-0">
                <div className="table-responsive">
                  <table className="table table-hover table-striped table-bordered align-middle mb-0">
                    <thead>
                      <tr className="text-soft">
                        <th>Name</th>
                        <th>Code</th>
                        <th>Public link</th>
                        <th>Country</th>
                        <th>Specialty</th>
                        <th>Rule</th>
                        <th>Period</th>
                        <th>Clinics</th>
                        <th>Active</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {circles.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="text-center text-soft py-5">
                            No lead circles yet. Click &quot;Add circle&quot; to create one.
                          </td>
                        </tr>
                      ) : (
                        circles.map((circle) => {
                          const publicLink = getCirclePublicLink(circle.code);
                          const copyKey = `row-${circle._id || circle.id}`;
                          return (
                          <tr key={circle._id || circle.id}>
                            <td><span className="fw-medium text-dark">{circle.name}</span></td>
                            <td><code className="text-primary">{circle.code}</code></td>
                            <td>
                              {circle.active === false ? (
                                <span className="text-soft small">Not public (deactivated)</span>
                              ) : publicLink ? (
                                <div className="d-flex align-items-center flex-g-1 flex-wrap">
                                  <code className="text-break small bg-light px-2 py-1 rounded me-1" style={{ maxWidth: "180px" }}>
                                    {publicLink}
                                  </code>
                                  <Button
                                    type="button"
                                    color="light"
                                    size="sm"
                                    className="btn-icon p-1"
                                    title="Copy link"
                                    onClick={() => handleCopyLink(publicLink, copyKey)}
                                  >
                                    <Icon name="copy" />
                                  </Button>
                                  {copyFeedback === copyKey && (
                                    <span className="text-success small">Copied!</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-soft">—</span>
                              )}
                            </td>
                            <td>{circle.country || "—"}</td>
                            <td>{circle.specialty || "—"}</td>
                            <td>{circle.distributionRule || "—"}</td>
                            <td>{circle.period || "—"}</td>
                            <td>{(Array.isArray(circle.clinicIds) ? circle.clinicIds.length : 0)}</td>
                            <td>
                              {circle.active !== false ? (
                                <span className="badge bg-success">Yes</span>
                              ) : (
                                <span className="badge bg-light text-dark">No</span>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="d-flex flex-g-1 justify-content-end">
                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() => openEdit(circle)}
                                  className="btn-icon"
                                  title="Edit"
                                >
                                  <Icon name="edit" />
                                </Button>
                                <Button
                                  color="light"
                                  size="sm"
                                  className="btn-icon text-danger"
                                  onClick={() => handleDeleteClick(circle)}
                                  title="Deactivate"
                                >
                                  <Icon name="trash" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </Block>

        <Modal isOpen={modalOpen} toggle={handleRequestCloseModal} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a href="#close" onClick={(e) => { e.preventDefault(); handleRequestCloseModal(); }} className="close">
              <Icon name="cross-sm" />
            </a>
            <h5 className="title mb-1">{editingCircle ? "Edit lead circle" : "Create a lead circle"}</h5>
            <p className="text-soft small mb-4">
              {editingCircle
                ? "Update the circle name, distribution rules, and which clinics receive leads."
                : "A lead circle groups clinics that share the same campaign. New leads are distributed among the clinics in this circle."}
            </p>
            <Form onSubmit={handleSubmit}>
              {saveError && (
                <div className="alert alert-danger mb-3" role="alert">
                  {saveError}
                </div>
              )}
              <Row>
                <Col size="12">
                  <h6 className="overline-title text-primary-alt mb-3">Circle identity</h6>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="circle-name">Circle name</Label>
                    <Input
                      id="circle-name"
                      type="text"
                      value={form.name}
                      onChange={(e) => onFormChange("name", e.target.value)}
                      placeholder="e.g. Turkey Dental"
                      required
                    />
                    <small className="text-soft">Display name for this circle.</small>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="circle-code">Unique code</Label>
                    <Input
                      id="circle-code"
                      type="text"
                      value={form.code}
                      onChange={(e) => onFormChange("code", e.target.value)}
                      placeholder="e.g. turkey-dental or turkey_dental (lowercase, numbers, hyphens, underscores)"
                      required
                      disabled={!!editingCircle}
                    />
                    {editingCircle ? (
                      <small className="text-soft">The code cannot be changed after creation.</small>
                    ) : (
                      <small className="text-soft">Used in URLs. Lowercase letters, numbers, hyphens and underscores (no spaces).</small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="circle-country">Country</Label>
                    <Input
                      id="circle-country"
                      type="text"
                      value={form.country}
                      onChange={(e) => onFormChange("country", e.target.value)}
                      placeholder="e.g. Turkey"
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="circle-specialty">Medical specialty (optional)</Label>
                    <Input
                      id="circle-specialty"
                      type="text"
                      value={form.specialty}
                      onChange={(e) => onFormChange("specialty", e.target.value)}
                      placeholder="e.g. dental"
                    />
                  </FormGroup>
                </Col>
                <Col size="12" className="mt-3">
                  <FormGroup>
                    <Label for="circle-form-options-source">Form options source (category group)</Label>
                    <RSelect
                      inputId="circle-form-options-source"
                      options={[
                        { value: "", label: "None (use specialty)" },
                        ...categoryOptions,
                      ]}
                      value={
                        form.formOptionsRootCategoryId
                          ? categoryOptions.find((o) => o.value === form.formOptionsRootCategoryId) || { value: form.formOptionsRootCategoryId, label: form.formOptionsRootCategoryId }
                          : { value: "", label: "None (use specialty)" }
                      }
                      onChange={(opt) => onFormChange("formOptionsRootCategoryId", opt?.value ?? "")}
                    />
                    <small className="text-soft">Category whose children are shown as &quot;What do you need?&quot; options on the circle landing. Leave &quot;None&quot; to use specialty filter.</small>
                  </FormGroup>
                </Col>
                <Col size="12" className="mt-3">
                  <FormGroup>
                    <Label className="text-soft small">Public landing link</Label>
                    {form.code && form.code.trim() ? (
                      <>
                        <div className="d-flex align-items-center flex-g-2 flex-wrap">
                          <Input
                            type="text"
                            value={getCirclePublicLink(form.code)}
                            readOnly
                            className="form-control bg-light font-monospace small"
                          />
                          <Button
                            type="button"
                            color="light"
                            size="sm"
                            onClick={() => handleCopyLink(getCirclePublicLink(form.code), "modal")}
                          >
                            <Icon name="copy" className="me-1" />
                            Copy link
                          </Button>
                          {copyFeedback === "modal" && (
                            <span className="text-success small fw-medium">Copied!</span>
                          )}
                        </div>
                        <small className="text-soft">Share this URL for campaigns (e.g. media buyer).</small>
                      </>
                    ) : (
                      <>
                        <div className="bg-light rounded px-3 py-2 text-soft small">
                          Save with a code to see the link
                        </div>
                        <Button type="button" color="light" size="sm" disabled className="mt-2">
                          <Icon name="copy" className="me-1" />
                          Copy link
                        </Button>
                        <small className="text-soft d-block mt-1">Enter and save a unique code above to get the public link.</small>
                      </>
                    )}
                  </FormGroup>
                </Col>

                <Col size="12" className="mt-4">
                  <h6 className="overline-title text-primary-alt mb-3">How leads are shared</h6>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="circle-rule">Distribution rule</Label>
                    <RSelect
                      inputId="circle-rule"
                      options={DISTRIBUTION_RULE_OPTIONS}
                      value={DISTRIBUTION_RULE_OPTIONS.find((o) => o.value === form.distributionRule)}
                      onChange={(opt) => onFormChange("distributionRule", opt?.value ?? "by_target")}
                    />
                    <small className="text-soft">How new leads are assigned to clinics in this circle.</small>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="circle-period">Target period</Label>
                    <RSelect
                      inputId="circle-period"
                      options={PERIOD_OPTIONS}
                      value={PERIOD_OPTIONS.find((o) => o.value === form.period)}
                      onChange={(opt) => onFormChange("period", opt?.value ?? "monthly")}
                    />
                    <small className="text-soft">How often lead targets are reset.</small>
                  </FormGroup>
                </Col>
                <Col size="12">
                  <FormGroup check className="mt-2">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={form.active}
                        onChange={(e) => onFormChange("active", e.target.checked)}
                      />
                      <span className="fw-medium">Circle is active</span>
                    </Label>
                    <small className="text-soft d-block ms-4">Inactive circles do not receive new leads.</small>
                  </FormGroup>
                </Col>

                <Col size="12">
                  <hr className="my-4" />
                  <h6 className="overline-title text-primary-alt mb-2">Clinics in this circle</h6>
                  <p className="text-soft small mb-3">Select the clinics that will share leads from this circle. Then set how many leads each clinic should receive below.</p>
                  <RSelect
                    isMulti
                    options={clinicOptions}
                    value={selectedClinicOptions}
                    onChange={onClinicsChange}
                    placeholder="Select one or more clinics..."
                  />
                </Col>

                {form.clinicIds.length > 0 && (
                  <Col size="12">
                    <h6 className="overline-title text-primary-alt mt-4 mb-2">Lead targets per clinic</h6>
                    <p className="text-soft small mb-3">Set how many leads each clinic should get in the chosen period. Target leads is required; other columns are optional.</p>
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Clinic</th>
                            <th>Target leads <span className="text-danger">*</span></th>
                            <th>Max leads</th>
                            <th>Weight</th>
                            <th>Priority</th>
                            <th>Budget cap (€)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.clinicIds.map((clinicId) => {
                            const t = form.targets[clinicId] || {};
                            return (
                              <tr key={clinicId}>
                                <td>{getClinicName(clinicId)}</td>
                                <td>
                                  <Input
                                    type="number"
                                    min={0}
                                    value={t.targetLeads ?? ""}
                                    onChange={(e) => onTargetChange(clinicId, "targetLeads", e.target.value)}
                                    placeholder="0"
                                  />
                                </td>
                                <td>
                                  <Input
                                    type="number"
                                    min={0}
                                    value={t.maxLeads ?? ""}
                                    onChange={(e) => onTargetChange(clinicId, "maxLeads", e.target.value)}
                                    placeholder="Optional"
                                  />
                                </td>
                                <td>
                                  <Input
                                    type="number"
                                    min={0}
                                    value={t.weight ?? ""}
                                    onChange={(e) => onTargetChange(clinicId, "weight", e.target.value)}
                                    placeholder="Optional"
                                  />
                                </td>
                                <td>
                                  <Input
                                    type="number"
                                    value={t.priority ?? ""}
                                    onChange={(e) => onTargetChange(clinicId, "priority", e.target.value)}
                                    placeholder="0"
                                  />
                                </td>
                                <td>
                                  <Input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={t.budgetCap ?? ""}
                                    onChange={(e) => onTargetChange(clinicId, "budgetCap", e.target.value)}
                                    placeholder="Optional"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Col>
                )}

                {/* ─── Media (Photos & Before & after) ───────────────────────── */}
                <Col size="12" className="mt-4">
                  <hr className="my-4" />
                  <h6 className="overline-title text-primary-alt mb-2">Media</h6>
                  <p className="text-soft small mb-3">
                    Banner (top of page), photos and before/after cases shown on the circle landing page. You can upload new files, choose from your library, or copy from another clinic.
                  </p>
                </Col>

                {/* Block: Banner (top of page) */}
                <Col size="12">
                  <div className="card card-bordered mb-3" style={{ borderLeft: "3px solid #6576ff" }}>
                    <div className="card-inner py-3 px-3">
                      <div className="d-flex align-items-center mb-2">
                        <Icon name="img" className="me-2 text-primary" />
                        <strong className="small">Banner</strong>
                      </div>
                      <p className="text-soft small mb-3">
                        One image at the top of your circle landing page. Upload a new image or choose from your library.
                      </p>
                      <div className="d-flex flex-wrap flex-g-2 mb-3 align-items-center">
                        <label className="btn btn-sm btn-outline-primary mb-0" style={{ cursor: uploadingBanner ? "wait" : "pointer" }}>
                          <input type="file" accept="image/*" onChange={handleUploadBannerFile} disabled={uploadingBanner} style={{ display: "none" }} />
                          <Icon name="upload" className="me-1" />
                          {uploadingBanner ? "Uploading…" : "Upload new image"}
                        </label>
                        <Button type="button" color="outline-primary" size="sm" onClick={() => setMediaSelectorOpen((prev) => ({ ...prev, banner: true }))}>
                          <Icon name="folder" className="me-1" />
                          Choose from library
                        </Button>
                        {form.bannerImageUrl && (
                          <Button type="button" color="light" size="sm" onClick={() => setForm((prev) => ({ ...prev, bannerImageUrl: "" }))} aria-label="Clear banner">
                            Clear banner
                          </Button>
                        )}
                      </div>
                      {form.bannerImageUrl && (
                        <div className="mt-2">
                          <img src={getMediaDisplayUrl(form.bannerImageUrl)} alt="Banner preview" className="rounded border" style={{ maxHeight: "120px", maxWidth: "100%", objectFit: "contain" }} />
                        </div>
                      )}
                    </div>
                  </div>
                </Col>

                {/* Block: Photos */}
                <Col size="12">
                  <div className="card card-bordered mb-3" style={{ borderLeft: "3px solid #6576ff" }}>
                    <div className="card-inner py-3 px-3">
                      <div className="d-flex align-items-center mb-2">
                        <Icon name="img" className="me-2 text-primary" />
                        <strong className="small">Photos</strong>
                        <span className="badge bg-light text-muted ms-2 small">{(form.photos || []).length}</span>
                      </div>
                      <p className="text-soft small mb-3">Add photos that will appear on your page. Upload new ones, pick from your library, or reuse photos from another clinic.</p>
                      <div className="d-flex flex-wrap flex-g-2 mb-3">
                        <label className="btn btn-sm btn-outline-primary mb-0" style={{ cursor: uploadingPhoto ? "wait" : "pointer" }}>
                          <input type="file" accept="image/*" onChange={handleUploadPhotoFile} disabled={uploadingPhoto} style={{ display: "none" }} />
                          <Icon name="upload" className="me-1" />
                          {uploadingPhoto ? "Uploading…" : "Upload new photos"}
                        </label>
                        <Button type="button" color="outline-primary" size="sm" onClick={() => setMediaSelectorOpen((prev) => ({ ...prev, photos: true }))}>
                          <Icon name="folder" className="me-1" />
                          Choose from library
                        </Button>
                        {editingCircle && (
                          <span className="d-inline-flex align-items-center flex-g-1 flex-wrap">
                            <span className="text-soft small">Use from another clinic:</span>
                            <RSelect
                              className="d-inline-block"
                              style={{ minWidth: "180px" }}
                              options={clinicOptions.filter((o) => o.value)}
                              value={copyFromClinic.clinicId ? clinicOptions.find((o) => o.value === copyFromClinic.clinicId) : null}
                              onChange={(opt) => setCopyFromClinic((prev) => ({ ...prev, clinicId: opt?.value ?? "" }))}
                              placeholder="Select clinic..."
                            />
                            <label className="mb-0 small d-inline-flex align-items-center me-1">
                              <input
                                type="checkbox"
                                checked={copyFromClinic.copyPhotos}
                                onChange={(e) => setCopyFromClinic((prev) => ({ ...prev, copyPhotos: e.target.checked }))}
                                className="me-1"
                              />
                              Photos
                            </label>
                            <label className="mb-0 small d-inline-flex align-items-center me-2">
                              <input
                                type="checkbox"
                                checked={copyFromClinic.copyBeforeAfter}
                                onChange={(e) => setCopyFromClinic((prev) => ({ ...prev, copyBeforeAfter: e.target.checked }))}
                                className="me-1"
                              />
                              Before & after cases
                            </label>
                            <Button type="button" color="primary" size="sm" onClick={handleCopyMediaFromClinic} disabled={copyingMedia || !copyFromClinic.clinicId}>
                              {copyingMedia ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                              Copy to this page
                            </Button>
                          </span>
                        )}
                      </div>
                      {(form.photos || []).length > 0 && (
                        <div className="d-flex flex-wrap flex-g-2 mt-2">
                          {(form.photos || []).map((url, index) => (
                            <div key={index} className="position-relative" style={{ width: "80px", height: "80px" }}>
                              <img src={getMediaDisplayUrl(url)} alt="" className="rounded border" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              <button
                                type="button"
                                className="btn btn-sm btn-icon btn-danger position-absolute top-0 end-0"
                                style={{ transform: "translate(50%, -50%)" }}
                                onClick={() => removePhoto(index)}
                                aria-label="Remove photo"
                              >
                                <Icon name="trash" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Col>

                {/* Block: Before & after */}
                <Col size="12">
                  <div className="card card-bordered mb-3" style={{ borderLeft: "3px solid #09c2de" }}>
                    <div className="card-inner py-3 px-3">
                      <div className="d-flex align-items-center mb-2">
                        <Icon name="img" className="me-2" style={{ color: "#09c2de" }} />
                        <strong className="small">Before & after</strong>
                        <span className="badge bg-light text-muted ms-2 small">{(form.beforeAfterCases || []).length}</span>
                      </div>
                      <p className="text-soft small mb-3">Add before-and-after cases. Upload new images, choose from your library, or copy from another clinic.</p>
                      <div className="d-flex flex-wrap flex-g-2 mb-3 align-items-center">
                        <label className="btn btn-sm btn-outline-primary mb-0" style={{ cursor: uploadingBeforeAfter ? "wait" : "pointer" }}>
                          <input type="file" accept="image/*" onChange={handleUploadBeforeAfterFile} disabled={uploadingBeforeAfter} style={{ display: "none" }} />
                          <Icon name="upload" className="me-1" />
                          {uploadingBeforeAfter ? "Uploading…" : "Upload new image"}
                        </label>
                        <Button type="button" color="outline-secondary" size="sm" onClick={addBeforeAfterCase}>
                          <Icon name="plus" className="me-1" />
                          Add case (enter URL)
                        </Button>
                        <Button type="button" color="outline-primary" size="sm" onClick={() => setMediaSelectorOpen((prev) => ({ ...prev, beforeAfter: true }))}>
                          <Icon name="folder" className="me-1" />
                          Choose from library
                        </Button>
                      </div>
                      {(form.beforeAfterCases || []).length > 0 && (
                        <div className="table-responsive">
                          <table className="table table-bordered table-sm">
                            <thead>
                              <tr>
                                <th>Order</th>
                                <th>Title (optional)</th>
                                <th>Image</th>
                                <th className="text-end">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(form.beforeAfterCases || [])
                                .slice()
                                .sort((a, b) => (Number(a.order) ?? 0) - (Number(b.order) ?? 0))
                                .map((c, idx) => {
                                  const index = form.beforeAfterCases.findIndex((x) => x === c);
                                  return (
                                    <tr key={index}>
                                      <td>
                                        <Input
                                          type="number"
                                          min={0}
                                          value={c.order ?? ""}
                                          onChange={(e) => updateBeforeAfterCase(index, "order", e.target.value)}
                                          className="form-control form-control-sm"
                                          style={{ width: "70px" }}
                                        />
                                      </td>
                                      <td>
                                        <Input
                                          type="text"
                                          value={c.title ?? ""}
                                          onChange={(e) => updateBeforeAfterCase(index, "title", e.target.value)}
                                          placeholder="Title (optional)"
                                          className="form-control form-control-sm"
                                        />
                                      </td>
                                      <td>
                                        <Input
                                          type="text"
                                          value={c.imageUrl ?? ""}
                                          onChange={(e) => updateBeforeAfterCase(index, "imageUrl", e.target.value)}
                                          placeholder="Image URL or path (from upload)"
                                          className="form-control form-control-sm"
                                        />
                                        {c.imageUrl && (
                                          <img src={getMediaDisplayUrl(c.imageUrl)} alt="" className="mt-1 rounded" style={{ maxHeight: "48px", maxWidth: "80px", objectFit: "contain" }} />
                                        )}
                                      </td>
                                      <td className="text-end">
                                        <Button type="button" color="danger" size="sm" outline onClick={() => removeBeforeAfterCase(index)} aria-label="Remove case">
                                          <Icon name="trash" />
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>

                {/* ─── Testimonial Videos ─────────────────────────────────── */}
                <Col size="12" className="mt-4 pt-3 border-top">
                  <h6 className="overline-title text-primary-alt mb-2">Testimonial videos</h6>
                  <p className="text-soft small mb-3">
                    Add patient testimonial video URLs (YouTube, local path, etc.). These will appear in the "What our patients say" section on the landing page. If left empty, the default fallback videos are used.
                  </p>
                  <div className="d-flex flex-wrap flex-g-2 mb-3">
                    <Button type="button" color="outline-primary" size="sm" onClick={addTestimonialVideo}>
                      <Icon name="plus" className="me-1" />
                      Add testimonial video
                    </Button>
                  </div>
                  {(form.testimonialVideos || []).length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-bordered table-sm">
                        <thead>
                          <tr>
                            <th style={{ width: "40%" }}>Video URL *</th>
                            <th style={{ width: "30%" }}>Title (displayed)</th>
                            <th style={{ width: "20%" }}>Thumbnail URL</th>
                            <th style={{ width: "10%" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(form.testimonialVideos || []).map((v, idx) => (
                            <tr key={idx}>
                              <td>
                                <Input
                                  type="text"
                                  value={v.url}
                                  onChange={(e) => updateTestimonialVideo(idx, "url", e.target.value)}
                                  placeholder="https://youtube.com/... or /videos/..."
                                  bsSize="sm"
                                />
                              </td>
                              <td>
                                <Input
                                  type="text"
                                  value={v.title}
                                  onChange={(e) => updateTestimonialVideo(idx, "title", e.target.value)}
                                  placeholder="e.g. Valentina · Italy"
                                  bsSize="sm"
                                />
                              </td>
                              <td>
                                <Input
                                  type="text"
                                  value={v.thumbnail}
                                  onChange={(e) => updateTestimonialVideo(idx, "thumbnail", e.target.value)}
                                  placeholder="Thumbnail URL (optional)"
                                  bsSize="sm"
                                />
                              </td>
                              <td className="text-end">
                                <Button type="button" color="danger" size="sm" outline onClick={() => removeTestimonialVideo(idx)} aria-label="Remove video">
                                  <Icon name="trash" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Col>

                <Col size="12" className="mt-4 pt-3 border-top">
                  <Button color="primary" type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Saving...
                      </>
                    ) : editingCircle ? (
                      "Save changes"
                    ) : (
                      "Create circle"
                    )}
                  </Button>
                  <Button type="button" color="light" className="ms-2" onClick={handleRequestCloseModal}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </ModalBody>
        </Modal>

        {/* MediaSelector modal: Banner */}
        <Modal isOpen={mediaSelectorOpen.banner} toggle={() => setMediaSelectorOpen((prev) => ({ ...prev, banner: false }))} size="lg" className="modal-dialog-centered">
          <ModalHeader toggle={() => setMediaSelectorOpen((prev) => ({ ...prev, banner: false }))}>
            Select banner image from your library
          </ModalHeader>
          <ModalBody>
            <MediaSelector
              onSelect={handleBannerFromLibrary}
              onCancel={() => setMediaSelectorOpen((prev) => ({ ...prev, banner: false }))}
              multiple={false}
              type="image"
              confirmButtonLabel="Use as banner"
            />
          </ModalBody>
        </Modal>

        {/* MediaSelector modal: Photos */}
        <Modal isOpen={mediaSelectorOpen.photos} toggle={() => setMediaSelectorOpen((prev) => ({ ...prev, photos: false }))} size="lg" className="modal-dialog-centered">
          <ModalHeader toggle={() => setMediaSelectorOpen((prev) => ({ ...prev, photos: false }))}>
            Select photos from your library
          </ModalHeader>
          <ModalBody>
            <MediaSelector
              onSelect={handlePhotosFromLibrary}
              onCancel={() => setMediaSelectorOpen((prev) => ({ ...prev, photos: false }))}
              multiple={true}
              type="image"
              confirmButtonLabel="Add selected photos"
            />
          </ModalBody>
        </Modal>

        {/* MediaSelector modal: Before & after */}
        <Modal isOpen={mediaSelectorOpen.beforeAfter} toggle={() => setMediaSelectorOpen((prev) => ({ ...prev, beforeAfter: false }))} size="lg" className="modal-dialog-centered">
          <ModalHeader toggle={() => setMediaSelectorOpen((prev) => ({ ...prev, beforeAfter: false }))}>
            Select images from your library
          </ModalHeader>
          <ModalBody>
            <MediaSelector
              onSelect={handleBeforeAfterFromLibrary}
              onCancel={() => setMediaSelectorOpen((prev) => ({ ...prev, beforeAfter: false }))}
              multiple={true}
              type="image"
              confirmButtonLabel="Add selected images"
            />
          </ModalBody>
        </Modal>

        <ConfirmModal
          isOpen={showDiscardConfirm}
          onClose={() => setShowDiscardConfirm(false)}
          onConfirm={handleConfirmDiscard}
          title="Discard changes?"
          message="You have unsaved changes. Discard them?"
          confirmText="Discard"
          cancelText="Cancel"
          type="warning"
          loading={false}
        />

        <ConfirmModal
          isOpen={deleteConfirm.isOpen}
          onClose={() => setDeleteConfirm({ isOpen: false, circle: null, loading: false })}
          onConfirm={handleDeleteConfirm}
          title="Deactivate lead circle"
          message={
            deleteConfirm.circle
              ? `Deactivate "${deleteConfirm.circle.name}"? The public URL will return 404. The record stays in the database (you can show deactivated circles and re-enable it by editing).`
              : ""
          }
          confirmText="Deactivate"
          type="danger"
          loading={deleteConfirm.loading}
        />
      </Content>
    </>
  );
};

export default LeadCircles;
